import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { NewSizingSnapshot } from '@/sizing/newSizingEngine';
import { motion } from 'framer-motion';
import { TrendingUp, Server, AlertCircle } from 'lucide-react';
interface PercentileCapacityChartProps {
  snapshot: NewSizingSnapshot | null;
  gpusPerServer: number;
}
interface PercentileLevel {
  label: string;
  percentile: number;
  load: number; // As percentage of P95
  servers: number;
  color: string;
  bgColor: string;
  description: string;
}
export const PercentileCapacityChart = ({
  snapshot,
  gpusPerServer
}: PercentileCapacityChartProps) => {
  if (!snapshot) {
    return <Card>
        <CardHeader>
          <CardTitle className="text-base">Capacity Scenarios</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 animate-pulse rounded bg-muted" />
        </CardContent>
      </Card>;
  }

  // Calculate server requirements at different percentiles
  const p95Servers = Math.ceil(snapshot.finalGpuCount / gpusPerServer);
  const percentiles: PercentileLevel[] = [{
    label: 'P25',
    percentile: 25,
    load: 0.4,
    // 40% of P95 load
    servers: Math.ceil(snapshot.finalGpuCount * 0.4 / gpusPerServer),
    color: 'hsl(142, 76%, 36%)',
    bgColor: 'hsl(142, 76%, 36%, 0.1)',
    description: 'Light usage'
  }, {
    label: 'P50',
    percentile: 50,
    load: 0.6,
    // 60% of P95 load
    servers: Math.ceil(snapshot.finalGpuCount * 0.6 / gpusPerServer),
    color: 'hsl(45, 93%, 47%)',
    bgColor: 'hsl(45, 93%, 47%, 0.1)',
    description: 'Typical usage'
  }, {
    label: 'P75',
    percentile: 75,
    load: 0.8,
    // 80% of P95 load
    servers: Math.ceil(snapshot.finalGpuCount * 0.8 / gpusPerServer),
    color: 'hsl(24, 95%, 53%)',
    bgColor: 'hsl(24, 95%, 53%, 0.1)',
    description: 'High usage'
  }, {
    label: 'P95',
    percentile: 95,
    load: 1.0,
    // 100% of P95 load
    servers: p95Servers,
    color: 'hsl(262, 83%, 58%)',
    bgColor: 'hsl(262, 83%, 58%, 0.1)',
    description: 'Peak capacity'
  }];
  const maxServers = Math.max(...percentiles.map(p => p.servers));
  const recommendedLevel = percentiles[3]; // P95

  return <Card className="overflow-hidden shadow-lg border-border/50 bg-gradient-to-br from-card via-card to-card/95">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            Capacity by Percentile
          </CardTitle>
          <Badge variant="secondary" className="text-xs">
            <Server className="h-3 w-3 mr-1" />
            {gpusPerServer} GPUs/server
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Server requirements at different load levels
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Chart Area */}
        <div className="space-y-3">
          {percentiles.map((level, idx) => {
          const heightPct = level.servers / maxServers * 100;
          const isRecommended = level.percentile === 95;
          return <motion.div key={level.label} className="space-y-1" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: idx * 0.15,
            duration: 0.5,
            ease: [0.4, 0, 0.2, 1]
          }}>
                {/* Label Row */}
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-foreground min-w-[32px]">
                      {level.label}
                    </span>
                    <span className="text-muted-foreground">{level.description}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground">
                      {(level.load * 100).toFixed(0)}% load
                    </span>
                    <span className="font-bold text-foreground min-w-[80px] text-right">
                      {level.servers} {level.servers === 1 ? 'server' : 'servers'}
                    </span>
                  </div>
                </div>

                {/* Bar */}
                <div className="relative h-12 w-full rounded-lg overflow-hidden" style={{
              backgroundColor: level.bgColor
            }}>
                  <motion.div className="absolute inset-y-0 left-0 rounded-lg shadow-md flex items-center justify-between px-4" style={{
                backgroundColor: level.color
              }} initial={{
                width: 0
              }} animate={{
                width: `${heightPct}%`
              }} transition={{
                delay: idx * 0.15 + 0.2,
                duration: 0.8,
                ease: [0.4, 0, 0.2, 1]
              }}>
                    <div className="flex items-center gap-2">
                      <Server className="h-4 w-4 text-white/90" />
                      {level.servers > 1 && <span className="text-white/90 text-sm font-semibold">
                          ×{level.servers}
                        </span>}
                    </div>
                    {isRecommended && <Badge variant="secondary" className="bg-white/20 text-white text-[10px] border-0">
                        Recommended
                      </Badge>}
                  </motion.div>

                  {/* Glow effect for recommended */}
                  {isRecommended && <motion.div className="absolute inset-0 rounded-lg" style={{
                boxShadow: `0 0 20px ${level.color}`,
                opacity: 0.3
              }} animate={{
                opacity: [0.3, 0.5, 0.3]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }} />}
                </div>
              </motion.div>;
        })}
        </div>

        {/* Summary Card */}
        <motion.div className="rounded-lg border border-border/50 bg-muted/30 p-3 space-y-2" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8,
        duration: 0.5
      }}>
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
            <div className="space-y-1 flex-1">
              <p className="text-xs font-semibold text-foreground">
                Sizing Recommendation
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                This system is sized for <span className="font-semibold text-foreground">{recommendedLevel.label}</span> capacity 
                with <span className="font-semibold text-foreground">{recommendedLevel.servers} servers</span>. 
                {' '}During typical usage (P50), you'll utilize ~{Math.round(percentiles[1].servers / recommendedLevel.servers * 100)}% of capacity.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Scale Reference */}
        
      </CardContent>
    </Card>;
};